import { useState, useRef } from 'react';
import { Bold, Italic, Heading, List, ListOrdered, Link, Quote, Code, Eye, Edit3 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { MarkdownPreview } from './MarkdownPreview';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function MarkdownEditor({ value, onChange, placeholder, className }: MarkdownEditorProps) {
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertText = (before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    onChange(newText);

    // 恢复光标位置
    setTimeout(() => {
      textarea.focus();
      const newCursor = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  const toolbar = [
    { icon: Bold, action: () => insertText('**', '**'), title: '加粗' },
    { icon: Italic, action: () => insertText('*', '*'), title: '斜体' },
    { icon: Heading, action: () => insertText('## ', ''), title: '标题' },
    { icon: List, action: () => insertText('- ', ''), title: '无序列表' },
    { icon: ListOrdered, action: () => insertText('1. ', ''), title: '有序列表' },
    { icon: Link, action: () => insertText('[', '](url)'), title: '链接' },
    { icon: Quote, action: () => insertText('> ', ''), title: '引用' },
    { icon: Code, action: () => insertText('```\n', '\n```'), title: '代码块' },
  ];

  return (
    <div className={cn('border border-gray-200 rounded-lg overflow-hidden', className)}>
      {/* 工具栏 */}
      <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 border-b border-gray-200">
        {toolbar.map((item, index) => (
          <button
            key={index}
            type="button"
            onClick={item.action}
            title={item.title}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
          >
            <item.icon className="w-4 h-4" />
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setPreview(!preview)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors',
            preview
              ? 'bg-blue-100 text-blue-600'
              : 'text-gray-600 hover:bg-gray-200'
          )}
        >
          {preview ? <Edit3 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {preview ? '编辑' : '预览'}
        </button>
      </div>

      {/* 编辑区 / 预览区 */}
      {preview ? (
        <div className="min-h-[300px] max-h-[500px] overflow-y-auto p-4 bg-white">
          <MarkdownPreview content={value} />
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || '支持 Markdown 语法...'}
          className="w-full min-h-[300px] max-h-[500px] p-4 resize-y bg-white text-gray-900 text-sm leading-relaxed focus:outline-none"
          spellCheck={false}
        />
      )}
    </div>
  );
}
